import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AdminLayout } from '../components/Layout/AdminLayout';
import { PageHeader } from '../components/common';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, IconButton, Tooltip, TextField, MenuItem, CircularProgress, Alert, Typography, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, Grid, Card, CardContent,
} from '@mui/material';
import { Search, Visibility, PlayArrow, CheckCircle, DoneAll, Add } from '@mui/icons-material';
import { issueService, type Issue, type IssueStatus } from '../services/issue.service';
import { useSnackbar } from '../contexts/SnackbarContext';
import { usePermission } from '../hooks';
import { colors } from '../theme/theme';

const STATUS_META: Record<string, { label: string; color: 'warning' | 'info' | 'success' | 'default' }> = {
  open: { label: 'Open', color: 'warning' },
  in_progress: { label: 'In progress', color: 'info' },
  resolved: { label: 'Resolved', color: 'success' },
  closed: { label: 'Closed', color: 'default' },
};

const CATEGORY_LABEL: Record<string, string> = {
  bribe: 'Bribery', 'wrong-charge': 'Wrong charge', technical: 'Technical', other: 'Other',
};

export const IssuesPage = () => {
  const { can } = usePermission();
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');
  const [view, setView] = useState<Issue | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ category: 'technical', subject: '', message: '' });

  const canManage = can('support.manage') || can('customers.manage') || can('customers.view');

  const { data, isLoading, error } = useQuery({
    queryKey: ['county-issues', { search, status, category }],
    queryFn: () => issueService.list({ search: search || undefined, status: status || undefined, category: category || undefined, per_page: 50 }),
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status, note }: { id: number; status: IssueStatus; note?: string }) =>
      issueService.updateStatus(id, status, note),
    onSuccess: (_r, v) => {
      queryClient.invalidateQueries({ queryKey: ['county-issues'] });
      showSnackbar(`Issue marked ${STATUS_META[v.status]?.label ?? v.status}`, 'success');
      setView(null);
    },
    onError: (e: any) => showSnackbar(e?.response?.data?.message || 'Failed to update issue', 'error'),
  });

  const createMutation = useMutation({
    mutationFn: () => issueService.create(form),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['county-issues'] });
      showSnackbar('Issue created', 'success');
      setCreateOpen(false);
      setForm({ category: 'technical', subject: '', message: '' });
    },
    onError: (e: any) => showSnackbar(e?.response?.data?.message || 'Failed to create issue', 'error'),
  });

  const issues = data?.data ?? [];
  const summary = data?.summary;

  const advance = (issue: Issue) => {
    // open -> in_progress -> resolved. Closed is a final manual step from detail.
    const next: IssueStatus = issue.status === 'open' ? 'in_progress' : 'resolved';
    statusMutation.mutate({ id: issue.id, status: next });
  };

  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title="Reported Issues"
          subtitle="Citizen reports (bribery, wrong charges, technical) and issues your team raises."
          breadcrumbs={[{ label: 'Dashboard', path: '/dashboard' }, { label: 'Issues' }]}
          action={canManage ? <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>Raise an issue</Button> : undefined}
        />

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {(['open', 'in_progress', 'resolved'] as const).map((k) => (
            <Grid size={{ xs: 12, sm: 4 }} key={k}>
              <Card><CardContent sx={{ py: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>{summary?.[k] ?? 0}</Typography>
                <Typography variant="body2" color="text.secondary">{STATUS_META[k].label}</Typography>
              </CardContent></Card>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <TextField size="small" placeholder="Search reference or text" value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <Search sx={{ mr: 1, color: colors.grey[500] }} fontSize="small" /> }} sx={{ minWidth: 280 }} />
          <TextField select size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 150 }}>
            <MenuItem value="">All</MenuItem>
            {Object.entries(STATUS_META).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Category" value={category} onChange={(e) => setCategory(e.target.value)} sx={{ minWidth: 150 }}>
            <MenuItem value="">All</MenuItem>
            {Object.entries(CATEGORY_LABEL).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
          </TextField>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3 }}>Failed to load issues.</Alert>}

        <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden' }}>
          {isLoading ? (
            <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: colors.grey[50] }}>
                    <TableCell sx={{ fontWeight: 600 }}>Reference</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Reporter</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Reported</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {issues.map((i) => {
                    const meta = STATUS_META[i.status] ?? { label: i.status, color: 'default' as const };
                    const rowBusy = statusMutation.isPending && statusMutation.variables?.id === i.id;
                    const isFinal = i.status === 'resolved' || i.status === 'closed';
                    return (
                      <TableRow key={i.id} hover>
                        <TableCell sx={{ fontWeight: 500 }}>{i.reference}</TableCell>
                        <TableCell>
                          <Chip label={CATEGORY_LABEL[i.category] ?? i.category} size="small"
                            color={i.category === 'bribe' ? 'error' : 'default'} variant="outlined" />
                        </TableCell>
                        <TableCell>{i.reporter_name}{i.reporter_type === 'county_admin' && ' (staff)'}</TableCell>
                        <TableCell>{new Date(i.created_at).toLocaleDateString()}</TableCell>
                        <TableCell><Chip label={meta.label} color={meta.color} size="small" /></TableCell>
                        <TableCell align="right">
                          <Tooltip title="View"><IconButton size="small" onClick={() => setView(i)} sx={{ color: colors.primary.main }}><Visibility fontSize="small" /></IconButton></Tooltip>
                          {canManage && !isFinal && (
                            <Tooltip title={i.status === 'open' ? 'Start working' : 'Mark resolved'}>
                              <span><IconButton size="small" disabled={rowBusy} onClick={() => advance(i)} sx={{ color: colors.success.main }}>
                                {i.status === 'open' ? <PlayArrow fontSize="small" /> : <CheckCircle fontSize="small" />}
                              </IconButton></span>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {issues.length === 0 && (
                    <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No issues found</Typography>
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {/* View / manage detail */}
        <Dialog open={!!view} onClose={() => setView(null)} maxWidth="sm" fullWidth>
          {view && (
            <>
              <DialogTitle>
                {view.reference}
                <Chip label={STATUS_META[view.status]?.label ?? view.status} color={STATUS_META[view.status]?.color ?? 'default'} size="small" sx={{ ml: 1 }} />
              </DialogTitle>
              <DialogContent>
                <Typography variant="caption" color="text.secondary">Category</Typography>
                <Typography variant="body2" sx={{ mb: 1.5 }}>{CATEGORY_LABEL[view.category] ?? view.category}</Typography>
                <Typography variant="caption" color="text.secondary">Reporter</Typography>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  {view.reporter_name}{view.contact_email ? ` · ${view.contact_email}` : ''}
                </Typography>
                {view.subject && (<><Typography variant="caption" color="text.secondary">Subject</Typography><Typography variant="body2" sx={{ mb: 1.5 }}>{view.subject}</Typography></>)}
                <Typography variant="caption" color="text.secondary">Details</Typography>
                <Typography variant="body2" sx={{ mb: 1.5, whiteSpace: 'pre-wrap' }}>{view.message}</Typography>
                {view.resolution_note && (<><Typography variant="caption" color="text.secondary">Resolution note</Typography><Typography variant="body2" sx={{ mb: 1.5 }}>{view.resolution_note}</Typography></>)}
              </DialogContent>
              {canManage && (
                <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 3, pb: 2 }}>
                  {view.status === 'open' && <Button startIcon={<PlayArrow />} onClick={() => statusMutation.mutate({ id: view.id, status: 'in_progress' })}>Start working</Button>}
                  {(view.status === 'open' || view.status === 'in_progress') && <Button color="success" startIcon={<CheckCircle />} onClick={() => statusMutation.mutate({ id: view.id, status: 'resolved' })}>Resolve</Button>}
                  {view.status !== 'closed' && <Button startIcon={<DoneAll />} onClick={() => statusMutation.mutate({ id: view.id, status: 'closed' })}>Close</Button>}
                </DialogActions>
              )}
            </>
          )}
        </Dialog>

        {/* Create */}
        <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Raise an issue</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField select label="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} fullWidth>
                {Object.entries(CATEGORY_LABEL).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
              </TextField>
              <TextField label="Subject (optional)" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} fullWidth />
              <TextField label="Details" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} multiline rows={4} fullWidth />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button variant="contained" disabled={createMutation.isPending || form.message.trim().length < 5} onClick={() => createMutation.mutate()}>Create</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
};
